import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { currency, num, dateShort } from "@/lib/format";
import { StatusBadge, CategoryBadge, RunBadge, SkuTag } from "@/components/Badges";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, User, Warehouse, Flame, PackageCheck, SlidersHorizontal } from "lucide-react";

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-stone-200 bg-white p-4">
    <div className="text-xs uppercase tracking-wide text-stone-400">{label}</div>
    <div className="text-xl font-extrabold font-mono mt-1 text-stone-900">{value}</div>
  </div>
);

export default function FacilityDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get(`/facilities/${id}`).then((r) => setF(r.data)).catch(() => setF(false)); }, [id]);

  if (f === false) return <div className="p-8 text-stone-400">Facility not found.</div>;
  if (!f) return <div className="p-8 text-stone-400">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
      <button data-testid="back-btn" onClick={() => navigate("/facilities")} className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors">
        <ArrowLeft className="h-4 w-4" />Back to Facilities
      </button>

      <div className="rounded-xl bg-white border border-stone-200/80 shadow-sm overflow-hidden">
        <div className="h-1.5" style={{ background: f.accent }} />
        <div className="p-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg flex items-center justify-center" style={{ background: `${f.accent}18`, color: f.accent }}>
              <Warehouse className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-stone-900">{f.name}</h1>
              <p className="text-sm text-stone-500">{f.type} · <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" />{f.manager}</span></p>
            </div>
          </div>
          <p className="text-sm text-stone-500 mt-3">{f.description}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
            <Stat label="Asset Value" value={currency(f.total_value)} />
            <Stat label="SKUs Held" value={num(f.items.length)} />
            <Stat label="Production Runs" value={num(f.production_runs.length)} />
            <Stat label="Receipts" value={num(f.receipts.length)} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="items">
        <TabsList className="bg-stone-100">
          <TabsTrigger value="items" data-testid="ftab-items"><Warehouse className="h-4 w-4 mr-1.5" />On-Hand Items</TabsTrigger>
          <TabsTrigger value="runs" data-testid="ftab-runs"><Flame className="h-4 w-4 mr-1.5" />Production</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="ftab-receipts"><PackageCheck className="h-4 w-4 mr-1.5" />Receipts</TabsTrigger>
          <TabsTrigger value="adj" data-testid="ftab-adj"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Adjustments</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                <th className="px-4 py-3 text-left">Item</th><th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
              <tbody>
                {f.items.map((it) => (
                  <tr key={it.sku_code} onClick={() => navigate(`/skus/${it.sku_code}`)} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 cursor-pointer">
                    <td className="px-4 py-3"><div className="font-medium text-stone-800">{it.name}</div><SkuTag code={it.sku_code} /></td>
                    <td className="px-4 py-3"><CategoryBadge category={it.category} /></td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{num(it.qty)} {it.unit}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{currency(it.value)}</td>
                    <td className="px-4 py-3"><StatusBadge status={it.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="runs">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            {f.production_runs.length === 0 ? <div className="text-center py-12 text-stone-400">No production runs here.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                  <th className="px-4 py-3 text-left">Batch</th><th className="px-4 py-3 text-left">Output</th><th className="px-4 py-3 text-right">Target</th>
                  <th className="px-4 py-3 text-right">Completed</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
                <tbody>
                  {f.production_runs.map((r) => (
                    <tr key={r.batch_no} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                      <td className="px-4 py-3"><SkuTag code={r.batch_no} /></td>
                      <td className="px-4 py-3"><SkuTag code={r.output_sku} /></td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{num(r.target_qty)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{num(r.completed_qty)}</td>
                      <td className="px-4 py-3 text-stone-600">{dateShort(r.scheduled_date)}</td>
                      <td className="px-4 py-3"><RunBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="receipts">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            {f.receipts.length === 0 ? <div className="text-center py-12 text-stone-400">No receipts here.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                  <th className="px-4 py-3 text-left">Receipt</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Vendor</th>
                  <th className="px-4 py-3 text-right">Items</th><th className="px-4 py-3 text-left">QA</th></tr></thead>
                <tbody>
                  {f.receipts.map((r) => (
                    <tr key={r.receipt_no} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                      <td className="px-4 py-3"><SkuTag code={r.receipt_no} /></td>
                      <td className="px-4 py-3 text-stone-600">{dateShort(r.date)}</td>
                      <td className="px-4 py-3 font-medium text-stone-800">{r.vendor}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{r.items.length}</td>
                      <td className="px-4 py-3"><RunBadge status={r.qa_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="adj">
          <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
            {f.adjustments.length === 0 ? <div className="text-center py-12 text-stone-400">No adjustments here.</div> : (
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
                  <th className="px-4 py-3 text-left">ID</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Item</th>
                  <th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-right">Delta</th></tr></thead>
                <tbody>
                  {f.adjustments.map((a) => (
                    <tr key={a.adj_no} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70">
                      <td className="px-4 py-3"><SkuTag code={a.adj_no} /></td>
                      <td className="px-4 py-3 text-stone-600">{dateShort(a.date)}</td>
                      <td className="px-4 py-3"><SkuTag code={a.sku_code} /></td>
                      <td className="px-4 py-3 text-stone-600">{a.type}</td>
                      <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${a.delta_qty >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{a.delta_qty >= 0 ? "+" : ""}{num(a.delta_qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
